import React, { useEffect, useMemo, useState } from 'react';
import SidebarMenu from './SidebarMenu';
import useMensajesNoLeidos from '../../../hooks/useMensajesNoLeidos';
import ParticularService, { PARTICULAR_PERMISOS_EVENT } from '../../../services/ParticularService';
import particularesApiService from '../../../services/ParticularesApiService';

const BASE_PARTICULAR_MENU_ITEMS = [
  { id: 'resumen', label: 'Resumen', icon: 'fas fa-chart-line' },
  // Administradores: toda la cuenta; profesor: sus cursos.
  { id: 'academico', label: 'Estado académico', icon: 'fas fa-chart-bar' },
  // "Modo colegio": tomar asistencia a un año/división (administradores y profesores de la división).
  { id: 'asistencias', label: 'Asistencias', icon: 'fas fa-clipboard-check', requiresModoColegio: true },
  { id: 'mensajes', label: 'Mensajes', icon: 'fas fa-envelope', path: '/mensajes', state: { scope: 'particulares' } },
  { id: 'calendario', label: 'Calendario docente', icon: 'fas fa-calendar-alt', requiresCalendarAccess: true },
  { id: 'etiquetas', label: 'Etiquetas', icon: 'fas fa-tags' },
  // Administradores de la cuenta (y admin general): todas las entidades agrupadas.
  {
    id: 'entidades',
    label: 'Entidades',
    icon: 'fas fa-database',
    requiresEntidadesAccess: true,
    defaultOpen: false,
    children: [
      { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
      { id: 'profesoresAsociados', label: 'Profesores', icon: 'fas fa-user-tie' },
      { id: 'cursos', label: 'Cursos', icon: 'fas fa-layer-group' },
      { id: 'divisiones', label: 'Años y divisiones', icon: 'fas fa-sitemap', requiresModoColegio: true },
      { id: 'aulas', label: 'Aulas', icon: 'fas fa-door-open' },
      { id: 'invitaciones', label: 'Invitaciones', icon: 'fas fa-ticket-alt' },
      { id: 'preceptores', label: 'Preceptores', icon: 'fas fa-user-shield' }
    ]
  },
  // Profesor común: sus cursos, alumnos y aulas sueltos (solo lectura).
  { id: 'cursos', label: 'Cursos', icon: 'fas fa-layer-group', hideWithEntidades: true },
  { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate', hideWithEntidades: true },
  { id: 'aulas', label: 'Aulas', icon: 'fas fa-door-open', hideWithEntidades: true },
  { id: 'cuentaCorriente', label: 'Cta. Cte.', icon: 'fas fa-file-invoice-dollar', requiresCuentaCorrienteAccess: true },
  { id: 'asistentes', label: 'Asistentes IA', icon: 'fas fa-robot', requiresChatGPTAccess: true }
];

const toExternalItem = (item) => {
  if (item.children) {
    return { ...item, children: item.children.map(toExternalItem) };
  }
  if (item.path) return item;
  return {
    ...item,
    path: '/particular',
    state: { activeMenu: item.id }
  };
};

// Lista plana (grupos + hijos) para buscar un ítem por id.
const flattenParticularMenuItems = (items) => items.flatMap((item) => (
  item.children ? [item, ...flattenParticularMenuItems(item.children)] : [item]
));

const buildParticularDashboardMenuItems = ({
  externalNavigation = false
} = {}) => (
  externalNavigation ? BASE_PARTICULAR_MENU_ITEMS.map(toExternalItem) : BASE_PARTICULAR_MENU_ITEMS
);

// Entidades y Cta. Cte. son de gestión de la cuenta: solo los ven el dueño, los administradores y el admin general.
// Mientras no se conocen los permisos quedan ocultos, para que un profesor no los vea aparecer y desaparecer.
const filterByPermisos = (items, permisos) => items
  .filter((item) => {
    if (item.requiresEntidadesAccess) return Boolean(permisos?.entidades);
    if (item.requiresModoColegio) return Boolean(permisos?.modoColegio);
    if (item.hideWithEntidades) return !permisos?.entidades;
    if (item.requiresProfesoresAccess) return Boolean(permisos?.profesores);
    if (item.requiresInvitacionesAccess) return Boolean(permisos?.invitaciones);
    if (item.requiresCuentaCorrienteAccess) return Boolean(permisos?.cuentaCorriente);
    return true;
  })
  .map((item) => (item.children ? { ...item, children: filterByPermisos(item.children, permisos) } : item));

let contextoRequest = null;

const useParticularPermisos = () => {
  const particularId = ParticularService.getActiveParticularId();
  const [permisos, setPermisos] = useState(() => ParticularService.getPermisos(particularId));

  useEffect(() => {
    const handleUpdate = () => setPermisos(ParticularService.getPermisos(ParticularService.getActiveParticularId()));
    window.addEventListener(PARTICULAR_PERMISOS_EVENT, handleUpdate);

    // Sin permisos en caché (por ejemplo, entrando directo a Mensajes): se piden una sola vez.
    if (!ParticularService.getPermisos(particularId)) {
      contextoRequest = contextoRequest || particularesApiService.getContexto()
        .catch(() => null)
        .finally(() => { contextoRequest = null; });
      contextoRequest.then(handleUpdate);
    }

    return () => window.removeEventListener(PARTICULAR_PERMISOS_EVENT, handleUpdate);
  }, [particularId]);

  return permisos;
};

const ParticularDashboardMenu = ({
  activeId,
  onNavigate,
  className = '',
  externalNavigation = false,
  messageScope = 'particular'
}) => {
  const { count: mensajesNoLeidos } = useMensajesNoLeidos(true, { scope: messageScope });
  const permisos = useParticularPermisos();
  const items = useMemo(() => filterByPermisos(buildParticularDashboardMenuItems({
    externalNavigation
  }), permisos).map((item) => (
    item.id === 'mensajes' ? { ...item, badge: mensajesNoLeidos } : item
  )), [
    externalNavigation,
    mensajesNoLeidos,
    permisos
  ]);

  return (
    <SidebarMenu
      items={items}
      activeId={activeId}
      onNavigate={onNavigate}
      className={className}
    />
  );
};

export { buildParticularDashboardMenuItems, flattenParticularMenuItems, BASE_PARTICULAR_MENU_ITEMS };
export default ParticularDashboardMenu;
